// ScenarioTable.tsx - Table view for scenarios with theme support

import React from 'react';
import { Scenario } from '../../types/ScenarioManagement';
import { useThemeUtils } from '../../hooks/useThemeUtils';

interface ScenarioTableProps {
  scenarios: Scenario[];
  onSort: (field: string) => void;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onEdit: (scenario: Scenario) => void;
  onDelete: (scenarioIds: string[]) => void;
}

const ScenarioTable: React.FC<ScenarioTableProps> = ({
  scenarios,
  onSort,
  sortBy,
  sortOrder,
  onEdit,
  onDelete
}) => {
  const { backgroundPatterns, textPatterns, themeClass } = useThemeUtils();

  const getSortIcon = (field: string) => {
    const iconColor = sortBy === field 
      ? themeClass({ light: 'text-professional-blue-600', dark: 'text-professional-blue-400' })
      : textPatterns.tertiary;

    if (sortBy !== field) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={iconColor}>
          <path d="M7 7l5-5 5 5M7 17l5 5 5-5" />
        </svg>
      );
    }
    
    return sortOrder === 'asc' ? (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={iconColor}>
        <path d="M7 17l5-5 5 5" />
      </svg>
    ) : (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={iconColor}>
        <path d="M7 7l5 5 5-5" />
      </svg>
    );
  };

  const getDifficultyColor = (difficulty: string): string => {
    switch (difficulty) {
      case 'Beginner': return themeClass({
        light: 'text-emerald-700 bg-emerald-100 border-emerald-200',
        dark: 'text-emerald-300 bg-emerald-900/30 border-emerald-700'
      });
      case 'Intermediate': return themeClass({
        light: 'text-yellow-700 bg-yellow-100 border-yellow-200',
        dark: 'text-yellow-300 bg-yellow-900/30 border-yellow-700'
      });
      case 'Advanced': return themeClass({
        light: 'text-orange-700 bg-orange-100 border-orange-200',
        dark: 'text-orange-300 bg-orange-900/30 border-orange-700'
      });
      case 'Expert': return themeClass({
        light: 'text-red-700 bg-red-100 border-red-200',
        dark: 'text-red-300 bg-red-900/30 border-red-700'
      });
      case 'Tech': return themeClass({
        light: 'text-blue-700 bg-blue-100 border-blue-200',
        dark: 'text-blue-300 bg-blue-900/30 border-blue-700'
      });
      case 'Exec': return themeClass({
        light: 'text-purple-700 bg-purple-100 border-purple-200',
        dark: 'text-purple-300 bg-purple-900/30 border-purple-700'
      });
      default: return themeClass({
        light: 'text-slate-600 bg-slate-100 border-slate-200',
        dark: 'text-slate-400 bg-slate-800/50 border-slate-600'
      });
    }
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(new Date(date));
  };


  const headerButtonClasses = themeClass({
    base: 'flex items-center gap-2 font-medium transition-colors',
    light: 'text-slate-700 hover:text-slate-900',
    dark: 'text-slate-300 hover:text-white'
  });

  const headerLabelClasses = `${textPatterns.primary} font-medium`;

  const rowClasses = themeClass({
    base: 'border-b transition-colors',
    light: 'border-slate-200 hover:bg-slate-50',
    dark: 'border-navy-600 hover:bg-navy-700/30'
  });


  const actionButtonClasses = themeClass({
    base: 'p-2 rounded-lg transition-colors',
    light: 'text-slate-600 hover:text-slate-900 hover:bg-slate-100',
    dark: 'text-slate-400 hover:text-slate-200 hover:bg-navy-600'
  });

  const deleteButtonClasses = themeClass({
    base: 'p-2 rounded-lg transition-colors',
    light: 'text-slate-600 hover:text-red-600 hover:bg-red-50',
    dark: 'text-slate-400 hover:text-red-400 hover:bg-red-900/30'
  });

  return (
    <div className={`${backgroundPatterns.card} rounded-2xl overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className={themeClass({
            base: 'border-b',
            light: 'bg-slate-50 border-slate-200',
            dark: 'bg-navy-800/50 border-navy-600'
          })}>
            <tr>
              <th className="p-4 text-left">
                <button
                  onClick={() => onSort('title')}
                  className={headerButtonClasses}
                >
                  Title
                  {getSortIcon('title')}
                </button>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => onSort('difficulty')}
                  className={headerButtonClasses}
                >
                  Difficulty
                  {getSortIcon('difficulty')}
                </button>
              </th>
              <th className="p-4 text-left">
                <span className={headerLabelClasses}>Category</span>
              </th>
              <th className="p-4 text-left">
                <span className={headerLabelClasses}>Status</span>
              </th>
              <th className="p-4 text-left">
                <button
                  onClick={() => onSort('updatedAt')}
                  className={headerButtonClasses}
                >
                  Last Updated
                  {getSortIcon('updatedAt')}
                </button>
              </th>
              <th className="p-4 text-left">
                <span className={headerLabelClasses}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {scenarios.map((scenario, index) => (
              <tr
                key={scenario.id}
                className={`${rowClasses} ${scenario.deletedAt ? 'opacity-60' : ''}`}
              >
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{scenario.icon}</span>
                    <div>
                      <h4 className={`font-semibold ${textPatterns.primary} line-clamp-1`}>
                        {scenario.title}
                      </h4>
                      <p className={`text-sm ${textPatterns.secondary} line-clamp-1`}>
                        {scenario.subtitle}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getDifficultyColor(scenario.difficulty)}`}>
                    {scenario.difficulty}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`text-sm ${textPatterns.secondary}`}>
                    {scenario.category}
                  </span>
                </td>
                <td className="p-4">
                  {!scenario.isAvailable && (
                    <span className={`text-sm font-medium ${textPatterns.tertiary}`}>
                      Coming Soon
                    </span>
                  )}
                  {scenario.deletedAt && (
                    <span className={`ml-2 text-xs ${themeClass({ light: 'text-red-600', dark: 'text-red-400' })}`}>
                      (Deleted)
                    </span>
                  )}
                </td>
                <td className="p-4">
                  <span className={`text-sm ${textPatterns.tertiary}`}>
                    {formatDate(scenario.updatedAt)}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onEdit(scenario)}
                      className={actionButtonClasses}
                      title="Edit scenario"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => onDelete([scenario.id])}
                      className={deleteButtonClasses}
                      title="Delete scenario"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {scenarios.length === 0 && (
              <tr>
                <td colSpan={6} className="p-8 text-center">
                  <div className={`${textPatterns.tertiary}`}>
                    <p className="text-lg font-medium mb-2">No scenarios found</p>
                    <p className="text-sm">Create your first scenario to get started.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScenarioTable;